import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CONSENT_KEY = 'vergo-cookie-consent';

type ConsentChoice = 'all' | 'essential' | null;

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(CONSENT_KEY, 'all');
    setVisible(false);
  };

  const handleEssentialOnly = () => {
    localStorage.setItem(CONSENT_KEY, 'essential');
    // Disable Google AdSense cookies by removing ad scripts
    try {
      const adScripts = document.querySelectorAll('script[src*="adsbygoogle"]');
      adScripts.forEach((s) => s.remove());
    } catch {}
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 inset-x-0 z-[9999] p-4"
        >
          <div className="max-w-4xl mx-auto rounded-xl border bg-card text-card-foreground shadow-lg p-5">
            <div className="flex items-start gap-3">
              <Cookie className="h-6 w-6 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 space-y-3">
                <p className="text-sm leading-relaxed">
                  We use cookies for essential site functionality and advertising (Google AdSense). 
                  Non-essential cookies help us show relevant ads and improve your experience. 
                  Read our{' '}
                  <Link to="/privacy-policy" className="text-primary underline font-medium">
                    Privacy Policy
                  </Link>{' '}
                  for details.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={handleAcceptAll}>
                    Accept All
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleEssentialOnly}>
                    Essential Only
                  </Button>
                </div>
              </div>
              <button
                onClick={handleEssentialOnly}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close cookie banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function getConsentChoice(): ConsentChoice {
  return (localStorage.getItem(CONSENT_KEY) as ConsentChoice) || null;
}
